import { AccountListingType, ClientBankAccountsLists, ClientCreditCardLists, toBool } from 'vopay';
import { AccountRow, AccountRowDefaults, BankAccountRow, CreditCardRow } from './types';
import { CREDIT_CARD_CURRENCY } from './constants';

// The row-building half of the two account listings (Account & Billing's payment methods and the
// client portal's Operational Accounts). Both turned the same bank-account + credit-card responses
// into the same three collections; only the default flags on each row were resolved differently.
// Kept a pure function — no hooks, no context — so either listing can use it regardless of how that
// listing fetches its data.
//
// Deliberately NOT here: the keyword/type search filter, the eLinx trigger, the selectable-accounts
// derivation and the open-drawer reconciliation. Those read and write component state, and each
// listing does them slightly differently; they stay with the callers.

export type BuildAccountRowsParams = {
  bankAccounts: ClientBankAccountsLists[];
  creditCards: ClientCreditCardLists[];
  /** The listing's own Purpose — rows whose `Purpose` contains it are "own", the rest are "other". */
  purpose: AccountListingType;
  /** The account's country; stamped on every row (the responses don't carry one). */
  country: string;
  /**
   * The default-resolution seam. Each listing keys its defaults off different responses — Billing
   * off the default *billing* bank account plus the account-wide default payment method, Operational
   * off the default *operational* bank account plus a default-credit-card object matched by
   * CreditCardToken — so the policy is data the caller passes in rather than a branch in here.
   *
   * All three flags are resolved by the caller because `IsDefault` is not derivable from the other
   * two: on the Billing listing a bank row is the default only when it is the default billing
   * account *and* the account's default payment method is 'bank'.
   *
   * Only own-purpose bank rows get resolved defaults — "other" rows never did (see below).
   */
  resolveBankDefaults: (bankAccount: ClientBankAccountsLists) => AccountRowDefaults;
  resolveCardDefaults: (creditCard: ClientCreditCardLists) => AccountRowDefaults;
};

/**
 * Plain arrays, where the legacy effects built objects keyed by array index and every consumer
 * immediately called `Object.values()` on them. Integer-keyed objects enumerate in ascending key
 * order, so the arrays hold the same rows in the same order.
 */
export type AccountRows = {
  /** Own-purpose bank rows, then every credit card — what the listing's table renders. */
  purposeRows: AccountRow[];
  /** Bank rows belonging to the *other* purpose, then every credit card. */
  otherRows: AccountRow[];
  /** Every row: other-purpose banks, then cards, then own-purpose banks. */
  allRows: AccountRow[];
};

/**
 * A row's identifier. Guaranteed to exist and to be unique: the row type is discriminated on `Type`,
 * and each response carries its own required token. Use it for React keys, for the details drawer's
 * lookup, and anywhere a row has to be named back to an API call.
 */
export function getAccountRowToken(row: AccountRow): string {
  return row.Type === 'Bank Account' ? row.Token : row.CreditCardToken;
}

// The account's country as its own routing details reveal it: FI + transit is a Canadian account, an
// ABA routing number a US one — the same per-row signal getFormattedAccountNumber keys the display
// format off. Preferred over the account-wide `country` the caller passes, because that is one value
// fanned onto every row: empty (or a guess) for a partner, whose bank accounts carry no country of
// their own. Falls back to the passed-in country when the row reveals neither — a foreign/IBAN
// account, and every credit-card row, which have no routing fields to read.
function getBankAccountCountry(bankAccount: ClientBankAccountsLists, fallbackCountry: string): string {
  if (bankAccount.FinancialInstitutionNumber || bankAccount.BranchTransitNumber) return 'CA';
  if (bankAccount.ABARoutingNumber) return 'US';

  return fallbackCountry;
}

function toBankAccountRow(
  bankAccount: ClientBankAccountsLists,
  country: string,
  defaults: AccountRowDefaults,
): BankAccountRow {
  return {
    ...bankAccount,
    Type: 'Bank Account',
    Country: getBankAccountCountry(bankAccount, country),
    // The response spells these two with a slash; the row doesn't.
    Province: bankAccount['Province/State'],
    PostalCode: bankAccount['PostalCode/ZipCode'],
    // And spells the bank identifier `BankID`, where every reader wants `BankId`. Nothing ever
    // mapped it, so the logo guard fell through and the one src that did render pointed at
    // `logos-banks/undefined.svg`.
    BankId: bankAccount.BankID,
    // IsDefaultAccount is the backend's own "default" string on the account record; the flags in
    // `defaults` are the portal's per-purpose view of it and come from the resolver. `defaults`
    // lands last because it also supplies the row's boolean `IsDefault`, replacing the response's
    // string of the same name.
    IsDefaultAccount: toBool(bankAccount.IsDefault),
    ...defaults,
  };
}

export default function buildAccountRows({
  bankAccounts,
  creditCards,
  purpose,
  country,
  resolveBankDefaults,
  resolveCardDefaults,
}: BuildAccountRowsParams): AccountRows {
  const ownBankRows: BankAccountRow[] = bankAccounts
    .filter((bankAccount) => bankAccount.Purpose?.split(',').includes(purpose))
    .map((bankAccount) => toBankAccountRow(bankAccount, country, resolveBankDefaults(bankAccount)));

  // "Other" rows are only ever offered as accounts to *add* to this listing, so they carry no
  // default flags at all: IsDefault falls back to the backend's own flag on the account record and
  // IsOperationalDefault / IsBillingDefault stay unset. Both legacy effects did exactly this.
  const otherBankRows: BankAccountRow[] = bankAccounts
    .filter((bankAccount) => !bankAccount.Purpose?.split(',').includes(purpose))
    .map((bankAccount) => toBankAccountRow(bankAccount, country, { IsDefault: toBool(bankAccount.IsDefault) }));

  // Cards appear in every collection — they belong to no Purpose — and the card response carries no
  // currency, so rows show the only currency cards are processed in today.
  const cardRows: CreditCardRow[] = creditCards.map((creditCard) => ({
    ...creditCard,
    Type: 'Credit Card',
    Country: country,
    Currency: CREDIT_CARD_CURRENCY,
    IsDefaultAccount: toBool(creditCard.IsDefault),
    ...resolveCardDefaults(creditCard),
  }));

  return {
    purposeRows: [...ownBankRows, ...cardRows],
    otherRows: [...otherBankRows, ...cardRows],
    allRows: [...otherBankRows, ...cardRows, ...ownBankRows],
  };
}
